import { useState, useEffect, useRef, useCallback } from 'react';

// 完了済み分析IDを追跡（モジュールレベルで永続化）
// 一度analysis_completedを受信した分析IDへの再接続を防ぐ
const completedAnalysisIds = new Set();

/**
 * WebSocket接続とリアルタイム更新用のReactフック
 * V2 Step1 分析の進捗をリアルタイムで受信
 */
export const useWebSocket = (path, options = {}) => {
  // pathが "/ws/..." で始まる場合は汎用WebSocketとして扱う
  // それ以外（analysisId）の場合は Step1 として扱う
  const isGeneric = path && path.startsWith('/ws/');

  // フックの呼び出し順序を一定に保つため、両方のフックを呼び出す
  const genericResult = useWebSocketGeneric(isGeneric ? path : null, options);
  const step1Result = useWebSocketStep1(
    !isGeneric && path ? path : null,
    options,
  );

  // 適切な結果を返す
  if (!path) {
    return {
      isConnected: false,
      connectionError: null,
      lastMessage: null,
      sendMessage: () => {},
      disconnect: () => {},
    };
  }

  return isGeneric ? genericResult : step1Result;
};

/**
 * 再接続の待機時間を計算（指数バックオフ + ジッター）
 * @param {number} attempt - 再接続試行回数
 * @param {number} baseDelay - 基本待機時間（ミリ秒）
 * @param {number} maxDelay - 最大待機時間（ミリ秒）
 */
const calculateBackoff = (attempt, baseDelay = 1000, maxDelay = 30000) => {
  // 指数バックオフ: baseDelay * 2^attempt
  const exponentialDelay = Math.min(baseDelay * Math.pow(2, attempt), maxDelay);
  // ジッターを追加（0-25%のランダム変動）
  const jitter = exponentialDelay * 0.25 * Math.random();
  return Math.floor(exponentialDelay + jitter);
};

/**
 * 汎用WebSocket接続フック（Step4など）
 */
const useWebSocketGeneric = (path, options = {}) => {
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState(null);
  const [lastMessage, setLastMessage] = useState(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = options.maxReconnectAttempts || 10;
  const reconnectDelay = options.reconnectDelay || 1000;
  const maxReconnectDelay = options.maxReconnectDelay || 30000;
  const enableAutoReconnect = options.enableAutoReconnect !== false;

  // ネットワーク状態の監視
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // WebSocket接続
  const connect = useCallback(() => {
    // pathがnullの場合は接続しない
    if (!path) {
      return;
    }

    // 既に接続中または接続済みの場合は何もしない
    if (wsRef.current) {
      const state = wsRef.current.readyState;
      if (state === WebSocket.OPEN || state === WebSocket.CONNECTING) {
        return;
      }
    }

    try {
      const token = localStorage.getItem('token');

      // WebSocket URLを動的に生成
      const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      let wsHost;
      if (window.location.hostname === 'localhost') {
        wsHost = 'localhost:8000';
      } else if (process.env.REACT_APP_API_URL) {
        try {
          const url = new URL(process.env.REACT_APP_API_URL);
          wsHost = url.host;
        } catch (e) {
          wsHost = window.location.host;
        }
      } else {
        wsHost = window.location.host;
      }
      const wsUrl = `${wsProtocol}//${wsHost}${path}${token ? `?token=${token}` : ''}`;
      wsRef.current = new WebSocket(wsUrl);

      wsRef.current.onopen = () => {
        setIsConnected(true);
        setConnectionError(null);
        reconnectAttempts.current = 0;

        if (options.onConnect) {
          options.onConnect();
        }
      };

      wsRef.current.onmessage = event => {
        setLastMessage(event.data);

        if (options.onMessage) {
          try {
            const data = JSON.parse(event.data);
            options.onMessage(data);
          } catch (e) {
            console.error('[WS Generic] メッセージパースエラー:', e);
          }
        }
      };

      wsRef.current.onclose = () => {
        setIsConnected(false);

        // オフラインの場合は再接続しない（オンライン復帰時に自動再接続）
        if (!navigator.onLine) {
          setConnectionError('オフラインです。ネットワーク接続を確認してください');
          return;
        }

        // 自動再接続（指数バックオフ）
        if (
          enableAutoReconnect &&
          reconnectAttempts.current < maxReconnectAttempts
        ) {
          const delay = calculateBackoff(
            reconnectAttempts.current,
            reconnectDelay,
            maxReconnectDelay
          );
          reconnectAttempts.current++;

          reconnectTimeoutRef.current = setTimeout(() => {
            connect();
          }, delay);
        } else {
          setConnectionError('WebSocket接続が切断されました');
        }
      };

      wsRef.current.onerror = () => {
        setConnectionError('WebSocket接続エラー');
      };
    } catch (error) {
      setConnectionError('WebSocket接続に失敗しました');
    }
  }, [
    path,
    enableAutoReconnect,
    maxReconnectAttempts,
    reconnectDelay,
    maxReconnectDelay,
    options,
  ]);

  // WebSocket切断
  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (wsRef.current && wsRef.current.readyState !== WebSocket.CLOSED) {
      // 再接続を防ぐため、oncloseを無効化
      wsRef.current.onclose = null;
      wsRef.current.close();
      wsRef.current = null;
    }
    setIsConnected(false);
  }, []);

  // メッセージ送信
  const sendMessage = useCallback(message => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
    }
  }, []);

  // 手動再接続
  const reconnect = useCallback(() => {
    reconnectAttempts.current = 0;

    // 既存の接続をクローズ
    if (wsRef.current) {
      wsRef.current.onclose = null; // 自動再接続を防ぐ
      if (wsRef.current.readyState !== WebSocket.CLOSED) {
        wsRef.current.close();
      }
      wsRef.current = null;
    }

    // タイムアウトをクリア
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    setIsConnected(false);

    // 少し遅延させてから再接続
    setTimeout(() => {
      connect();
    }, 500);
  }, [connect]);

  // 初期化とクリーンアップ
  useEffect(() => {
    if (path) {
      connect();
    }

    return () => {
      disconnect();
    };
    // connectとdisconnectは安定した参照を持つため、依存配列から除外
    // pathが変わったときのみ再接続
    // eslint-disable-next-line
  }, [path]);

  // オンライン復帰時に再接続
  useEffect(() => {
    // 再接続カウンターが最大値以上の場合は再接続しない
    if (
      isOnline &&
      path &&
      !isConnected &&
      reconnectAttempts.current > 0 &&
      reconnectAttempts.current < maxReconnectAttempts
    ) {
      // オンライン復帰時は再接続カウンターをリセットして再接続
      reconnectAttempts.current = 0;
      setConnectionError(null);
      connect();
    }
    // eslint-disable-next-line
  }, [isOnline, path, isConnected]);

  return {
    isConnected,
    connectionError,
    lastMessage,
    sendMessage,
    disconnect,
    reconnect,
    isOnline,
  };
};

/**
 * Step1専用WebSocket接続フック（既存のロジック）
 */
const useWebSocketStep1 = (analysisId, options = {}) => {
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState(null);
  const [analysisProgress, setAnalysisProgress] = useState(null);
  const [stepData, setStepData] = useState({});
  const [realtimeUpdates, setRealtimeUpdates] = useState([]);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = options.maxReconnectAttempts || 10;
  const reconnectDelay = options.reconnectDelay || 1000;
  const maxReconnectDelay = options.maxReconnectDelay || 30000;
  const enableAutoReconnect = options.enableAutoReconnect !== false;

  // ネットワーク状態の監視
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // メッセージハンドラー
  const messageHandlers = useRef({
    analysis_progress: data => {
      setAnalysisProgress(data);
      if (options.onProgress) {
        options.onProgress(data);
      }

      // data_updateが含まれている場合、onDataUpdateコールバックも呼び出す
      if (data.data_update && options.onDataUpdate) {
        // stepDataも更新
        if (data.data_update.type) {
          setStepData(prev => ({
            ...prev,
            [data.data_update.type]: data.data_update.data,
          }));
        }
        options.onDataUpdate(data);
      }
    },
    step_completed: data => {
      if (data.data_update) {
        setStepData(prev => ({
          ...prev,
          [data.data_update.type]: data.data_update.data,
        }));
      }
      if (options.onStepCompleted) {
        options.onStepCompleted(data);
      }
      // ステップ完了にもdata_updateが内包されるため、即時マージ経路も併用
      if (data.data_update && options.onDataUpdate) {
        try {
          options.onDataUpdate(data);
        } catch (e) {
          console.warn('onDataUpdate callback error (step_completed):', e);
        }
      }
    },
    data_update: data => {
      if (data.data_update) {
        setStepData(prev => ({
          ...prev,
          [data.data_update.type]: data.data_update.data,
        }));
      }
      setRealtimeUpdates(prev => [...prev, data]);
      if (options.onDataUpdate) {
        options.onDataUpdate(data);
      }
    },
    connection_established: data => {
      setIsConnected(true);
      setConnectionError(null);
      reconnectAttempts.current = 0;
      if (options.onConnect) {
        options.onConnect(data);
      }
    },
    error: data => {
      console.error('WebSocketエラー:', data);
      setConnectionError(data.message);
      if (options.onError) {
        options.onError(data);
      }
    },
    pong: () => {
      // ping-pongによる接続確認
    },
    analysis_completed: data => {
      // 分析完了通知（完了済み分析でWebSocket接続した場合）
      console.log('🔧 [WebSocket] Received analysis_completed, stopping reconnection for:', analysisId);
      
      // この分析IDを完了済みとしてマーク（モジュールレベルで永続化）
      if (analysisId) {
        completedAnalysisIds.add(analysisId);
      }
      
      // 自動再接続を停止
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }

      // 再接続カウンターを最大値+1に設定して自動再接続を完全に防ぐ
      reconnectAttempts.current = maxReconnectAttempts + 1;

      // WebSocket接続を閉じる
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }

      if (options.onAnalysisCompleted) {
        options.onAnalysisCompleted(data);
      }
    },
  });

  // WebSocket接続
  const connect = useCallback(() => {
    // analysisIdがない場合は接続しない
    if (!analysisId) {
      return;
    }
    
    // この分析IDが既に完了済みの場合は接続しない（モジュールレベルでチェック）
    if (completedAnalysisIds.has(analysisId)) {
      console.log('🔧 [WebSocket] Skipping connect: analysis already completed:', analysisId?.substring(0, 30) + '...');
      return;
    }

    // 既に接続中または接続済みの場合はスキップ
    if (wsRef.current?.readyState === WebSocket.OPEN || 
        wsRef.current?.readyState === WebSocket.CONNECTING) {
      return;
    }
    
    // 再接続が無効化されている場合（analysis_completed後など）はスキップ
    if (reconnectAttempts.current > maxReconnectAttempts) {
      console.log('🔧 [WebSocket] Skipping connect: reconnect disabled (analysis completed)');
      return;
    }

    try {
      // JWTトークンを取得
      const token = localStorage.getItem('token');

      // WebSocket URLを動的に生成
      const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      // STG/本番ではAPIのFQDNにWebSocketを向ける（例: test-api.magicss.ai）
      // REACT_APP_API_URL があればそこからホスト名を取得。なければ従来どおり。
      let wsHost;
      if (window.location.hostname === 'localhost') {
        wsHost = 'localhost:8000';
      } else if (process.env.REACT_APP_API_URL) {
        try {
          const url = new URL(process.env.REACT_APP_API_URL);
          wsHost = url.host; // includes hostname[:port]
        } catch (e) {
          wsHost = window.location.host;
        }
      } else {
        wsHost = window.location.host;
      }
      const wsUrl = `${wsProtocol}//${wsHost}/ws/v2/step1/analysis/${analysisId}${token ? `?token=${token}` : ''}`;

      wsRef.current = new WebSocket(wsUrl);

      wsRef.current.onopen = () => {
        setIsConnected(true);
        setConnectionError(null);
        reconnectAttempts.current = 0;

        // 即時にonConnectを発火（サーバーがconnection_establishedを送らない場合のフォールバック）
        try {
          if (options.onConnect) {
            options.onConnect({ analysis_id: analysisId, via: 'onopen' });
          }
        } catch (e) {
          console.warn('onConnect callback error:', e);
        }

        // サブスクライブ要求とステータス要求を送信
        try {
          wsRef.current.send(
            JSON.stringify({
              type: 'subscribe_updates',
              analysis_id: analysisId,
            }),
          );
          wsRef.current.send(
            JSON.stringify({ type: 'get_status', analysis_id: analysisId }),
          );
        } catch (e) {
          console.warn('初期メッセージ送信失敗:', e);
        }

      };

      wsRef.current.onmessage = event => {
        try {
          const data = JSON.parse(event.data);
          const handler = messageHandlers.current[data.type];
          if (handler) {
            handler(data);
          }
        } catch (error) {
          console.error('WebSocketメッセージ解析エラー:', error);
        }
      };

      wsRef.current.onclose = () => {
        setIsConnected(false);

        // オフラインの場合は再接続しない（オンライン復帰時に自動再接続）
        if (!navigator.onLine) {
          setConnectionError('オフラインです。ネットワーク接続を確認してください');
          return;
        }

        // 自動再接続（指数バックオフ）
        if (
          enableAutoReconnect &&
          reconnectAttempts.current < maxReconnectAttempts
        ) {
          const delay = calculateBackoff(
            reconnectAttempts.current,
            reconnectDelay,
            maxReconnectDelay
          );
          reconnectAttempts.current++;

          reconnectTimeoutRef.current = setTimeout(() => {
            connect();
          }, delay);
        } else {
          setConnectionError('WebSocket接続が切断されました');
        }
      };

      wsRef.current.onerror = () => {
        setConnectionError('WebSocket接続エラー');
      };
    } catch (error) {
      setConnectionError('WebSocket接続に失敗しました');
    }
  }, [analysisId, enableAutoReconnect, maxReconnectAttempts, reconnectDelay, maxReconnectDelay]);

  // WebSocket切断
  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
    }
    if (wsRef.current) {
      wsRef.current.close();
    }
    setIsConnected(false);
  }, []);

  // ping送信
  const sendPing = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(
        JSON.stringify({
          type: 'ping',
          timestamp: new Date().toISOString(),
        }),
      );
    }
  }, []);

  // メッセージ送信
  const sendMessage = useCallback(message => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
    }
  }, []);

  // 分析状態取得
  const getAnalysisStatus = useCallback(() => {
    sendMessage({
      type: 'get_status',
      analysis_id: analysisId,
    });
  }, [analysisId, sendMessage]);

  // 初期化とクリーンアップ
  useEffect(() => {
    if (analysisId) {
      // 新しい分析IDの場合、再接続カウンターをリセット
      reconnectAttempts.current = 0;
      
      connect();

      // 定期的なping
      pingIntervalRef.current = setInterval(sendPing, 30000); // 30秒間隔
    }

    return () => {
      disconnect();
    };
  }, [analysisId]);

  // オンライン復帰時に再接続
  useEffect(() => {
    // 再接続カウンターが最大値以上の場合は再接続しない（analysis_completed後など）
    if (
      isOnline &&
      analysisId &&
      !isConnected &&
      reconnectAttempts.current > 0 &&
      reconnectAttempts.current < maxReconnectAttempts
    ) {
      // オンライン復帰時は再接続カウンターをリセットして再接続
      reconnectAttempts.current = 0;
      setConnectionError(null);
      connect();
    }
    // eslint-disable-next-line
  }, [isOnline, analysisId, isConnected]);

  // 手動再接続
  const reconnect = useCallback(() => {
    // disconnect を直接呼ぶのではなく、処理をインライン化
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
    }
    if (wsRef.current) {
      wsRef.current.close();
    }
    setIsConnected(false);

    // 再接続
    reconnectAttempts.current = 0;
    setTimeout(() => {
      // connectを直接呼ばず、connect関数の処理をインライン化
      if (!analysisId) {
        return;
      }

      if (wsRef.current?.readyState === WebSocket.OPEN) {
        return;
      }

      connect();
    }, 1000);
  }, [analysisId, connect]);

  // analysisIdがnullの場合は空の結果を返す（すべてのフックの後）
  if (!analysisId) {
    return {
      isConnected: false,
      connectionError: null,
      analysisProgress: null,
      stepData: {},
      realtimeUpdates: [],
      sendMessage: () => {},
      getAnalysisStatus: () => {},
      reconnect: () => {},
      disconnect: () => {},
    };
  }

  return {
    isConnected,
    connectionError,
    analysisProgress,
    stepData,
    realtimeUpdates,
    sendMessage,
    getAnalysisStatus,
    reconnect,
    disconnect,
    isOnline,
  };
};

export default useWebSocket;
